'use strict';
import { PrimaryButton } from '@client/new/components/button';
import { ProgramSummaryTabProps } from '@client/new/pages/collection/program';
import {
  MenuItem,
  Select,
  TextField,
  Typography,
  Button,
  Tooltip,
} from '@mui/material';
import { Program } from '@playbook-technologies/common-enums';
import React from 'react';
import { browserHistory } from 'react-router';
import Faq from '../Faq';
import MediaEditor from '../MediaEditor';
import { SelectWithNull } from '../SelectWithNull';
import StatusBar from '../StatusBar';
import TagEditor from '../TagEditor';
import auth from '../auth';
import { ColorPicker } from '../common/inputs/ColorPicker';
import { Toggle } from '../common/inputs/Toggle';
import { SettingsSection } from './SettingsSection';
import { CollectionType } from './CollectionType';
import PillSelect, { SquarePill } from './PillSelect';
import ProgramReviews from './ProgramReviews';
import { COLLECTION_SETTINGS } from './collections.settings';
import { CollectionProgram } from './collections.types';
import { trackEvent } from '@services/tracking';
import * as enums from '@playbook-technologies/common-enums';
import { sprinkles } from '@design-system/sprinkles.css';
import { clsx } from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/pro-regular-svg-icons';
import { faCircleInfo } from '@fortawesome/pro-solid-svg-icons';
import { Col, Row } from '@design-system/components/grid/grid';
import { PATHS } from '@client/routes';
import otpPublishingBehaviorImage from '/static/images/otp-publishing-behaviour.png';
import coverImageInfo from '/static/images/cover-image-info.png';

type ProgramEditState = {
  isModified: boolean;
  isSaving: boolean;
};

export class ProgramForm extends React.Component<
  ProgramSummaryTabProps,
  ProgramEditState
> {
  constructor(props) {
    super(props);
    this.state = {
      isModified: false,
      isSaving: false,
    };
  }

  getInfluencerId = () => {
    const influencerId = this.props.params.influencerId
      ? this.props.params.influencerId
      : auth.getUser().influencerId;
    if (!influencerId) return this.props.collection.influencerId;
    else return influencerId;
  };

  merge = (newData: Partial<CollectionProgram>) => {
    const data = { ...this.props.collection, ...newData };
    this.setState({ isModified: true });
    this.props.onChange(data);
  };

  toggleSection = (name: 'faq' | 'testimonials') => {
    const currentValues = this.props.collection.hiddenSections || {};
    this.merge({
      hiddenSections: {
        ...this.props.collection.hiddenSections,
        [name]: !currentValues[name],
      },
    });

    if (name === 'faq') {
      trackEvent({
        type: 'FAQ Toggled',
        payload: {
          'Collection Type':
            this.props.collection.type === enums.Program.Type.Program.value
              ? 'Program'
              : 'Category',
        },
      });
    }

    if (name === 'testimonials') {
      trackEvent({
        type: 'Reviews Toggled',
        payload: {
          'Collection Type':
            this.props.collection.type === enums.Program.Type.Program.value
              ? 'Program'
              : 'Category',
        },
      });
    }
  };

  validate = (attribute: keyof CollectionProgram, newData = undefined) => {
    const data = newData ? newData : this.props.collection;
    // console.log(`[ProgramEdit] Validating '${attribute}'.`, data);

    if (this.props.validationTree) {
      // check whether attribute inside validation tree is valid, if not return 'invalid' CSS class
      // console.log(`[ProgramEdit] Validating attribute '${attribute}'...`);
      if (
        this.props.validationTree &&
        this.props.validationTree[attribute] &&
        this.props.validationTree[attribute].length > 0
      ) {
        console.log(`[ProgramEdit] Attribute '${attribute}' is invalid.`);
        return 'invalid' as const;
      }
    }

    return null;
  };
  publishingBehaviourChanged = (value) => {
    this.merge({
      publishingBehaviour: value,
    });
  };
  mediaIntroChanged = (mediaIntro) => {
    this.merge({ mediaIntro });
    trackEvent({
      type: 'Intro Video Uploaded',
      payload: {
        'Collection Type': 'Program',
      },
    });
  };
  mediaCoverChanged = (mediaCover) => {
    this.merge({ mediaCover });
    trackEvent({
      type: 'Cover Image Uploaded',
      payload: {
        'Collection Type': 'Program',
      },
    });
  };
  colorChanged = (hex) => {
    this.merge({ color: hex.slice(1) });

    trackEvent({
      type: 'Color Selected',
      payload: {
        'Collection Type': 'Program',
      },
    });
  };
  nameChanged = (ev) => {
    this.merge({ name: ev.target.value });
  };
  accessTypeChanged = (accessType: string) => {
    this.merge({ accessType });
  };
  inAppPurchaseChanged = (inAppPurchase: 'allow' | 'hide') => {
    this.merge({ inAppPurchase });
  };
  isLockedChanged = (isLocked: boolean) => {
    this.merge({ isLocked });
  };
  descriptionChanged = (ev) => {
    this.merge({ description: ev.target.value });
  };
  selectionChanged = (name, ev) => {
    this.merge({ [name]: ev.target.value });
  };
  contentStructureChanged = (value) => {
    if (value === Program.ContentStructure.Dayweek.value) {
      this.merge({
        contentStructure: value,
        publishingBehaviour: Program.PublishingBehaviour.Bottom.value,
      });
    } else {
      this.merge({ contentStructure: value });
    }
  };
  typeChanged = (type) => {
    this.merge({ type });
    this.props.typeChanged(type);
  };
  durationChanged = (ev) => {
    this.merge({
      duration: [parseInt(ev.target.value), this.props.collection.duration[1]],
    });
  };
  durationUnitChanged = (ev) => {
    this.merge({
      duration: [this.props.collection.duration[0], ev.target.value],
    });
    trackEvent({
      type: 'Length Specified',
      payload: {
        'Duration Type': this.props.collection.duration[1],
        'Duration Length': this.props.collection.duration[0],
      },
    });
  };
  tagsChanged = (tags) => {
    this.merge({ tags });

    trackEvent({
      type: 'Category Added',
      payload: {
        'Tag Location': 'Program',
        'Category Type': tags.length > 0 ? tags[tags.length - 1] : '',
      },
    });
  };
  targetAreaChanged = (targetArea) => {
    this.merge({ targetArea });

    trackEvent({
      type: 'Target Area Selected',
      payload: {
        'Tag Location': 'Program',
        'Target Area Type': targetArea,
      },
    });
  };
  handleGoalChanged = (values) => {
    this.merge({ goal: values[0] });
    trackEvent({
      type: 'Goal Selected',
      payload: {
        Goal: values[0],
      },
    });
  };
  handleDaysPerWeekChanged = (value) => {
    this.merge({ daysPerWeek: parseInt(value) });
    trackEvent({
      type: 'Days/Week Specified',
      payload: {
        'Duration Length': parseInt(value),
        'Duration Type': 'days',
      },
    });
  };
  handleSessionDurationChanged = (sessionDuration) => {
    this.merge({ sessionDuration });
    trackEvent({
      type: 'Mins/Session Specified',
      payload: {
        'Range Type': sessionDuration,
      },
    });
  };
  locationChanged = (values) => {
    this.merge({ location: values[0] });

    trackEvent({
      type: 'Location Selected',
      payload: {
        'Location Type': values[0],
        'Tag Location': 'Program',
      },
    });
  };
  difficultyChanged = (values) => {
    this.merge({ difficulty: values[0] });

    trackEvent({
      type: 'Difficulty Selected',
      payload: {
        'Difficulty Type': values[0],
        'Tag Location': 'Program',
      },
    });
  };
  equipmentChanged = (requiredEquipment) => {
    this.merge({ requiredEquipment });

    trackEvent({
      type: 'Equipment Added',
      payload: {
        'Equipment Type': requiredEquipment,
        'Tag Location': 'Program',
      },
    });
  };

  faqChanged = (faq) => {
    this.merge({ faq });

    trackEvent({
      type: 'FAQ Added',
      payload: {
        'Collection Type': 'Program',
      },
    });
  };
  reviewsChanged = (testimonials) => {
    this.merge({ testimonials });

    trackEvent({
      type: 'Reviews Added',
      payload: {
        'Collection Type': 'Program',
      },
    });
  };

  goBack = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const salesTypeParam = urlParams.get('salesType');
    const routeToByType =
      salesTypeParam === 'otp' ? PATHS.PURCHASES : PATHS.SUBSCRIPTIONS;
    if (!this.props.params.influencerId) {
      browserHistory.push('/');
      return;
    }
    if (salesTypeParam) {
      browserHistory.push(
        routeToByType(Number(this.props.params.influencerId)),
      );
      return;
    }
    browserHistory.push(
      PATHS.PARTNER_WORKOUTS(Number(this.props.params.influencerId)),
    );
  };

  getGoalOptions = () => {
    const options = COLLECTION_SETTINGS.PROGRAM_GOALS.map((g) => ({
      value: g,
      name: g,
    }));

    const existingGoal = this.props.collection.goal;

    if (
      existingGoal &&
      !COLLECTION_SETTINGS.PROGRAM_GOALS.includes(existingGoal)
    ) {
      options.push({
        value: existingGoal,
        name: existingGoal,
      });
    }

    return options;
  };

  render = () => {
    const isReadonly = this.props.readonly;

    const durationUnit =
      this.props.collection && this.props.collection.duration
        ? this.props.collection.duration[1]
        : 'weeks';

    let color = '#6367EF';
    if (
      this.props.collection.color &&
      this.props.collection.color.length === 6
    ) {
      color = `#${this.props.collection.color}`;
    }

    const isSaving = false;

    const viewAnaliticsAvailable =
      this.props?.collection?.id &&
      this.props?.collection?.accessType ===
        Program.AccessType.Exclusive.value &&
      this.props?.collection?.isVisible;

    const save = () =>
      this.props.save().then(() => this.setState({ isModified: false }));

    const showOtpBanner =
      this.props.collection.accessType === Program.AccessType.Exclusive.value;

    const creationMode = this.props.creationMode;

    const noEditMode =
      this.props.originalAccessType === 'exclusive'
        ? !!this.props.collection.publishedAt
        : false;

    return (
      <div className="programEdit">
        <main id="main">
          <div key="video">
            <section>
              {!showOtpBanner ? null : (
                <div className="flex bg-[#E5F6FD] gap-2 px-4 py-3.5 rounded-[10px] mb-8 max-w-[772px]">
                  <div>
                    <FontAwesomeIcon
                      icon={faCircleInfo}
                      className="text-[#448AF7] text-m"
                    />
                  </div>
                  <div className="flex flex-col">
                    <Typography className="font-medium">
                      The program will not be accessible to subscribers, unless
                      purchased.
                    </Typography>
                    <Typography className="font-medium">
                      The program will be accessible from the “Purchases” tab in
                      the consumer app.
                    </Typography>
                  </div>
                </div>
              )}
              <div
                className="flex flex-row gap-3 justify-end"
                style={{ float: 'right' }}
              >
                <Col>
                  <Row style={{ justifyContent: 'flex-end' }}>
                    {viewAnaliticsAvailable && (
                      <div className="button-box">
                        <Button
                          variant="outlined"
                          href="/admin/analytics#otps"
                          target="_blank"
                        >
                          Analyze purchases
                        </Button>
                      </div>
                    )}
                    {isReadonly ? null : (
                      <div
                        className={
                          'button-box' +
                          (isSaving ? ' pointer-events-none' : '')
                        }
                      >
                        <PrimaryButton
                          isLoading={isSaving}
                          onClick={() => save()}
                          disabled={!this.state.isModified}
                        >
                          {creationMode ? 'Create' : 'Save'}
                        </PrimaryButton>
                      </div>
                    )}
                  </Row>
                  {this.state.isModified && !isReadonly ? (
                    <span className="text-red-500 font-semibold text-center">
                      You have unsaved changes!
                    </span>
                  ) : (
                    <></>
                  )}
                </Col>
              </div>
            </section>

            <section>
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-5 pb-5">
                {this.props.isSellThroughHidden ? (
                  <div />
                ) : (
                  <div className="flex flex-col lg:flex-row lg:items-center gap-5">
                    <TextField
                      fullWidth
                      label="Sell through"
                      name="accessType"
                      disabled={noEditMode}
                      onChange={(e) => this.accessTypeChanged(e.target.value)}
                      select
                      value={this.props.collection.accessType}
                    >
                      <MenuItem value={Program.AccessType.Premium.value}>
                        Subscription
                      </MenuItem>
                      <MenuItem value={Program.AccessType.Exclusive.value}>
                        One-Time Purchase
                      </MenuItem>
                    </TextField>
                    {this.props.collection.accessType !==
                      Program.AccessType.Exclusive.value && (
                      <CollectionType
                        disabled={
                          this.props.disabledCollectionType || isReadonly
                        }
                        type={this.props.collection.type}
                        onChange={this.typeChanged}
                      />
                    )}
                  </div>
                )}
              </div>
              <div className="flex flex-col md:flex-row gap-3">
                <div>
                  <div className="heading-block">
                    <h3>Intro video (optional)</h3>
                  </div>
                  <div
                    className={`media-box square ${this.validate(
                      'mediaIntro',
                    )}`}
                  >
                    <MediaEditor
                      className="vertical"
                      metadata={this.props.collection.mediaIntro}
                      onChange={this.mediaIntroChanged}
                      onRemove={isReadonly ? null : this.mediaIntroChanged}
                      format="video"
                      accept="video/*"
                    />
                  </div>
                </div>
                <div>
                  <div className="heading-block">
                    <div className="flex items-center justify-between">
                      <h3>
                        Cover image{' '}
                        {creationMode ? null : (
                          <strong style={{ color: 'red' }}>*</strong>
                        )}
                      </h3>
                      <Tooltip
                        title={<CoverImageInfo />}
                        placement="right-start"
                        componentsProps={{
                          tooltip: {
                            open: true,
                            sx: {
                              bgcolor: 'black',
                              color: 'white',
                              fontSize: '14px',
                              fontFamily: 'inherit',
                              borderRadius: '10px',
                              width: 'max-content',
                              height: '220px',
                              p: 0,
                            },
                          },
                        }}
                      >
                        <FontAwesomeIcon icon={faInfoCircle} />
                      </Tooltip>
                    </div>
                  </div>
                  <div
                    className={`media-box square ${this.validate(
                      'mediaCover',
                    )}`}
                  >
                    <MediaEditor
                      className="square"
                      metadata={this.props.collection.mediaCover}
                      onChange={this.mediaCoverChanged}
                      onRemove={isReadonly ? null : this.mediaCoverChanged}
                      format="image"
                      accept="image/*"
                    />
                  </div>
                </div>
              </div>
              <h2 className="big-heading"></h2>
              <div>
                <div className="input-holder flex flex-col max-w-xl">
                  <TextField
                    label="Title"
                    required
                    type="text"
                    disabled={isReadonly}
                    onChange={this.nameChanged}
                    className={`${this.validate('name')}`}
                    value={this.props.collection.name}
                    inputProps={{ maxLength: 50 }}
                    InputLabelProps={{
                      required: true,
                      sx: {
                        '& .MuiFormLabel-asterisk': {
                          color: 'red',
                        },
                      },
                    }}
                  />
                </div>
                <div className="input-holder max-w-xs">
                  <label>
                    Color{' '}
                    {creationMode ? null : (
                      <strong style={{ color: 'red' }}>*</strong>
                    )}
                  </label>
                  <ColorPicker
                    value={'#' + this.props.collection.color}
                    onChange={this.colorChanged}
                    showColorBox
                  />
                </div>
                <div className="input-holder flex flex-col max-w-xl">
                  <TextField
                    multiline
                    label="Description"
                    disabled={isReadonly}
                    onChange={this.descriptionChanged}
                    value={this.props.collection.description}
                    inputProps={{ maxLength: 2000 }}
                  />
                </div>
                <section className="input-holder  py-6">
                  <Typography variant="h3">Duration</Typography>
                  <div className="flex gap-2">
                    <div
                      style={{
                        display: 'flex',
                        flexWrap: 'nowrap',
                        justifyContent: 'flex-start',
                      }}
                    >
                      <div className={`input-holder`} style={{ flexGrow: 1 }}>
                        <label
                          style={{
                            borderBottom: this.validate('duration')
                              ? '1px solid red'
                              : 'none',
                          }}
                        >
                          Program Length{' '}
                          {creationMode ? null : (
                            <strong style={{ color: 'red' }}>*</strong>
                          )}
                        </label>
                        <br />
                        <TextField
                          className="reps w-[88.3px]"
                          type="number"
                          value={this.props.collection.duration?.[0]}
                          onChange={this.durationChanged}
                          disabled={isReadonly}
                        />
                        &nbsp;&nbsp;
                        <Select
                          className="small"
                          value={durationUnit}
                          onChange={this.durationUnitChanged}
                          disabled={isReadonly}
                        >
                          <MenuItem value="days">days</MenuItem>
                          <MenuItem value="weeks">weeks</MenuItem>
                          <MenuItem value="months">months</MenuItem>
                        </Select>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <CustomSelectInput
                      label="Days/Week"
                      required={false}
                      disabled={isReadonly}
                      invalid={this.validate('daysPerWeek')}
                      value={this.props.collection.daysPerWeek}
                      onChange={this.handleDaysPerWeekChanged}
                      options={optionsRange(7)}
                      nullOptionDisabled={false}
                    />
                    <CustomSelectInput
                      label="Min/Session"
                      required={false}
                      disabled={isReadonly}
                      invalid={this.validate('sessionDuration')}
                      value={this.props.collection.sessionDuration}
                      onChange={this.handleSessionDurationChanged}
                      options={COLLECTION_SETTINGS.PROGRAM_SESSION_DURATION}
                      nullOptionDisabled={false}
                    />
                  </div>
                </section>
                <div>
                  <div>
                    <Typography variant="h3">Details</Typography>
                  </div>
                  <div className="space-y-4">
                    <PillSelect
                      invalid={this.validate('goal')}
                      label="Goal"
                      disabled={isReadonly}
                      required={!creationMode}
                      values={this.props.collection.goal}
                      options={this.getGoalOptions()}
                      onChange={this.handleGoalChanged}
                    />
                    <TextField
                      className="w-[200px]"
                      value={this.props.collection.goal || ''}
                      onChange={(e) => this.merge({ goal: e.target.value })}
                      placeholder="Select or type goal here"
                      inputProps={{ maxLength: 40 }}
                      disabled={isReadonly}
                    />
                    <div>
                      <PillSelect
                        invalid={this.validate('tags')}
                        label="Category"
                        disabled={isReadonly}
                        multi
                        limit={3}
                        values={this.props.collection.tags}
                        options={COLLECTION_SETTINGS.COLLECTION_TAGS.map(
                          (g) => ({
                            value: g,
                            name: g,
                          }),
                        )}
                        onChange={this.tagsChanged}
                      />
                    </div>
                    <div>
                      <PillSelect
                        invalid={this.validate('targetArea')}
                        label="Target Area"
                        disabled={isReadonly}
                        multi
                        limit={2}
                        values={this.props.collection.targetArea}
                        options={COLLECTION_SETTINGS.COLLECTION_TARGET_AREA.map(
                          (g) => ({
                            value: g,
                            name: g,
                          }),
                        )}
                        onChange={this.targetAreaChanged}
                      />
                    </div>
                    <div className="input-holder">
                      <label>Equipment</label>
                      <TagEditor
                        onChange={this.equipmentChanged}
                        disabled={isReadonly}
                        tags={this.props.collection.requiredEquipment || []}
                        placeholder="Type keyword here..."
                        maxLength={30}
                        maxItems={7}
                        allowSpaces={true}
                      />
                      <div className="mt-2">
                        <small>
                          Press Enter after typing to add new one <br />
                          Example: bands, kettlebell, mat, basketball
                        </small>
                      </div>
                    </div>

                    <PillSelect
                      invalid={this.validate('difficulty')}
                      label="Difficulty"
                      disabled={isReadonly}
                      Pill={SquarePill}
                      values={this.props.collection.difficulty}
                      options={COLLECTION_SETTINGS.COLLECTION_DIFFICULTY}
                      onChange={this.difficultyChanged}
                      gap={0}
                    />
                    <PillSelect
                      invalid={this.validate('location')}
                      label="Location"
                      disabled={isReadonly}
                      Pill={SquarePill}
                      values={this.props.collection.location}
                      options={COLLECTION_SETTINGS.COLLECTION_LOCATION}
                      onChange={this.locationChanged}
                      gap={0}
                    />
                  </div>
                </div>
              </div>
            </section>

            <section className="py-6">
              <Typography variant="h3">Settings</Typography>
              <div className="max-w-xl py-3">
                <ContentStructureSelect
                  contentStructure={this.props.collection.contentStructure}
                  disabled={isReadonly}
                  invalid={this.validate('contentStructure')}
                  onChange={this.contentStructureChanged}
                />
                <PublishingBehaviourSelect
                  publishingBehaviour={
                    this.props.collection.publishingBehaviour
                  }
                  disabled={isReadonly}
                  invalid={this.validate('publishingBehaviour')}
                  onChange={this.publishingBehaviourChanged}
                />
              </div>
              <SettingsSection
                program={this.props.collection}
                influencerId={this.getInfluencerId()}
                save={this.props.save}
                onInAppPurchaseChange={this.inAppPurchaseChanged}
                onIsLockedChange={(isLocked) => this.merge({ isLocked })}
              />
            </section>

            <section className="relative py-6">
              <Typography variant="h3">FAQ (optional)</Typography>
              <div className="absolute right-6 top-6 py-4">
                <Toggle
                  value={!this.props.collection.hiddenSections?.faq}
                  onChange={() => this.toggleSection('faq')}
                />
              </div>
              <div className="py-3">
                <Faq
                  faqSuggestions={COLLECTION_SETTINGS.PROGRAM_FAQ_SUGGESTIONS}
                  questions={this.props.collection.faq}
                  onChange={this.faqChanged}
                  tree={
                    this.props.validationTree && this.props.validationTree.faq
                      ? this.props.validationTree.faq
                      : null
                  }
                />
              </div>
            </section>

            <section className="relative  py-6">
              <div className="big-heading">
                <div
                  style={{
                    borderBottom: this.validate('testimonials')
                      ? '1px solid red'
                      : undefined,
                  }}
                >
                  <Typography variant="h3">Reviews (optional)</Typography>
                </div>
              </div>
              <div className="absolute right-6 top-6">
                <Toggle
                  value={!this.props.collection.hiddenSections?.testimonials}
                  onChange={() => this.toggleSection('testimonials')}
                />
              </div>
              <div>
                <ProgramReviews
                  onChange={this.reviewsChanged}
                  value={this.props.collection.testimonials || []}
                  errors={[]}
                />
              </div>
            </section>
          </div>
        </main>
        <StatusBar.Component />
      </div>
    );
  };
}

function CustomSelectInput({
  onChange,
  disabled,
  value,
  label,
  options,
  required,
  invalid,
  nullOptionDisabled = true,
}: any) {
  return (
    <div className={`input-holder`}>
      <div>
        <label
          style={{
            borderBottom: invalid ? '1px solid red' : 'none',
          }}
        >
          {label}&nbsp;{required && <strong style={{ color: 'red' }}>*</strong>}
        </label>
      </div>
      <SelectWithNull
        value={value}
        onChange={onChange}
        disabled={disabled}
        nullOptionDisabled={nullOptionDisabled}
      >
        {options.map((o) => (
          <MenuItem key={o.value} value={o.value}>
            {o.name}
          </MenuItem>
        ))}
      </SelectWithNull>
    </div>
  );
}

export function CustomNumberInput({
  onChange,
  disabled,
  value,
  label,
  required,
  invalid,
  max = 100,
  min = 1,
  inputProps = {},
  labelStyle = {},
}: any) {
  return (
    <div className={`input-holder`}>
      <div>
        <label
          style={{
            borderBottom: invalid ? '1px solid red' : 'none',
          }}
        >
          <span style={labelStyle}>{label}</span>&nbsp;
          {required && <strong style={{ color: 'red' }}>*</strong>}
        </label>
      </div>
      <TextField
        // temporary fix for the legacy styles
        InputProps={{
          classes: {
            root: 'w-[88px] clear-input-style p-0 bg-white rounded-md',
          },
        }}
        type="number"
        value={value}
        onChange={(e) => onChange(Math.min(Number(e.target.value), max))}
        disabled={disabled}
        min={min}
        max={max}
        {...inputProps}
      />
    </div>
  );
}

function optionsRange(length) {
  return Array.from({ length }).map((v, i) => ({
    value: i + 1,
    name: (i + 1).toString(),
  }));
}

const structureOptions = [
  {
    title: 'Week/Day',
    details: 'Workouts are assigned to a specific day and week',
    example: '(e.g. W1 D1, W1 D2...)',
    value: Program.ContentStructure.Dayweek.value,
  },
  {
    title: 'Series',
    details: 'Workouts are simply chronologically ordered',
    example: '(e.g. Workout 1, Workout 2...)',
    value: Program.ContentStructure.Sequential.value,
  },
];

function ContentStructureSelect({
  contentStructure,
  disabled,
  invalid,
  onChange,
}: any) {
  return (
    <div className={`input-holder }`}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-start',
          marginBottom: '10px',
          flexDirection: 'column',
        }}
      >
        <div style={{ borderBottom: invalid ? '1px solid red' : 'none' }}>
          <label style={{ marginBottom: 10 }}>Select structure</label>
        </div>
        <div className="flex gap-6">
          {structureOptions.map((option) => (
            <div
              className={clsx(
                'flex flex-col gap-2 px-4 py-2 rounded-lg cursor-pointer',
                contentStructure !== option.value &&
                  sprinkles({
                    borderWidth: 'xs',
                    borderColor: 'Secondary-200',
                    m: '1',
                  }),
                contentStructure === option.value &&
                  sprinkles({ borderWidth: 's', borderColor: 'B-100', m: '0' }),
              )}
              key={option.title}
              onClick={() => !disabled && onChange(option.value)}
            >
              <Typography>{option.title}</Typography>
              <Typography>
                <Typography className="w-[200px] text-[14px]">
                  {option.details}
                </Typography>
                <Typography className="w-[200px] text-[14px]">
                  {option.example}
                </Typography>
              </Typography>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function PublishingBehaviourSelect({
  invalid,
  disabled,
  publishingBehaviour,
  onChange,
}: any) {
  return (
    <div className={`input-holder`}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          marginBottom: '10px',
        }}
      >
        <div
          style={{ borderBottom: invalid ? '1px solid red' : 'none' }}
          className="flex items-center gap-2"
        >
          <label style={{ marginBottom: 0 }}>Publishing behavior</label>
          <Tooltip
            title={<PublishingBehaviourInfo />}
            placement="right-start"
            componentsProps={{
              tooltip: {
                open: true,
                sx: {
                  bgcolor: 'black',
                  color: 'white',
                  fontSize: '14px',
                  fontFamily: 'inherit',
                  borderRadius: '10px',
                  width: 'max-content',
                  p: 0,
                },
              },
            }}
          >
            <FontAwesomeIcon icon={faInfoCircle} />
          </Tooltip>
        </div>
      </div>
      <SelectWithNull
        className="small p-2"
        disabled={disabled}
        value={publishingBehaviour}
        onChange={onChange}
        nullOptionDisabled={true}
      >
        <MenuItem value={Program.PublishingBehaviour.Bottom.value}>
          Bottom
        </MenuItem>
        <MenuItem value={Program.PublishingBehaviour.Top.value}>Top</MenuItem>
      </SelectWithNull>
    </div>
  );
}

const PublishingBehaviourInfo = () => (
  <div className="flex gap-2 w-[420px]">
    <div className="flex flex-col gap-2 w-[200px] p-4">
      <Typography className="font-medium text-sm">
        Publishing behavior
      </Typography>
      <Typography className="font-normal text-sm">
        This is the order in which new workouts you add appear in the program on
        the consumer app.
      </Typography>
    </div>
    <div className="w-[200px] h-[200px] pr-4 bg-black rounded-[10px]">
      <img src={otpPublishingBehaviorImage} className="w-[200px] h-[200px]" />
    </div>
  </div>
);

const CoverImageInfo = () => (
  <div className="flex gap-2 w-[420px]">
    <div className="flex flex-col gap-2 w-[200px] p-4">
      <Typography className="font-medium text-sm">Cover image</Typography>
      <div className="flex flex-col">
        <Typography className="font-normal text-sm">
          Displayed in Discovery and Carousel. Create motivating and engaging
          content to capture your audience's attention. Enhance your cover
          images with{' '}
        </Typography>
        <a
          href="https://www.canva.com/design/DAEpjB3PXY4/xtlpMvdXkcbqVNS9MZJzww/view?utm_content=DAEpjB3PXY4&utm_campaign=designshare&utm_medium=link&utm_source=publishsharelink&mode=preview"
          target="_blank"
          rel="noreferrer"
          className={clsx('underline', sprinkles({ color: 'P-60' }))}
        >
          Canva templates
        </a>
      </div>
    </div>
    <div className="w-[200px] h-[220px] pr-4 pt-5 bg-black rounded-[10px]">
      <img src={coverImageInfo} className="w-[200px] h-[200px]" />
    </div>
  </div>
);
